export default function TimingYourSaleWithLeases() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-calendar-check-line"></i>
            <span>Strategic Timing</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Timing Your Sale with Lease Cycles
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Strategic timing of your sale relative to lease expirations and renewal cycles can significantly impact property value and deal success.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Optimal Timing Strategies</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              The relationship between your sale timeline and tenant lease cycles is one of the most overlooked factors in apartment building sales. Poor timing can cost you tens of thousands of dollars in reduced value or extended time on market.
            </p>

            <div className="space-y-4 mb-8">
              <div className="bg-green-50 border-l-4 border-green-500 p-5 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-calendar-check-line text-green-600 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-green-900 mb-2">Best Time: 6-12 Months Remaining on Leases</h4>
                    <p className="text-green-800 text-sm mb-3">
                      Properties with most leases having 6-12 months remaining are most attractive to buyers. This provides income stability without locking buyers into long-term commitments.
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-slate-600">
                        <strong>Action:</strong> Time your listing so that after 60-90 day marketing period, most leases have 6-12 months left at closing
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-amber-600 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-2">Avoid: Multiple Leases Expiring During Sale</h4>
                    <p className="text-amber-800 text-sm mb-3">
                      If 30%+ of leases expire during your anticipated marketing and closing period, expect buyer concerns and potential price reductions.
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-slate-600">
                        <strong>Solution:</strong> Renew expiring leases 3-6 months before listing, or delay listing until after renewal season
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-calendar-event-line text-blue-600 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Consider: Seasonal Rental Market Cycles</h4>
                    <p className="text-blue-800 text-sm mb-3">
                      List when rental demand is strong in your market. High occupancy and waiting lists demonstrate property desirability to buyers.
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-slate-600">
                        <strong>Typical Peak Seasons:</strong> Spring/Summer for family properties, Fall for student housing, Year-round for urban professionals
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">12-Month Sale Planning Timeline</h3>

            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                  12-9
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Months Before Sale</p>
                  <p className="text-slate-600 text-xs">Review lease expiration schedule. Begin renewing leases that will expire during anticipated sale period. Address problem tenants.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                  9-6
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Months Before Sale</p>
                  <p className="text-slate-600 text-xs">Complete deferred maintenance. Maximize occupancy. Ensure all leases are at market rates. Organize all documentation.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                  6-3
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Months Before Sale</p>
                  <p className="text-slate-600 text-xs">Finalize property improvements. Conduct pre-listing lease audit. Prepare rent roll and financial statements. Engage broker and attorney.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                  3-0
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Months Before Sale</p>
                  <p className="text-slate-600 text-xs">List property. Market to buyers. Conduct showings. Negotiate offers. Complete due diligence. Close transaction.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/f75afc445a48790bb79d10654b562b33.jpg"
                alt="Strategic timing and lease cycle planning for apartment building sale"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border border-slate-100 max-w-xs">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-calendar-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">6-12</div>
                  <div className="text-sm text-slate-600">Months Ideal</div>
                </div>
              </div>
              <p className="text-xs text-slate-600">
                Optimal remaining lease term for maximum buyer appeal
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Lease Timing Impact on Sale Price</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Lease Situation</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Buyer Appeal</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Price Impact</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Time on Market</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">80%+ leases with 6-12 months remaining</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                      <i className="ri-star-fill"></i>
                      Excellent
                    </span>
                  </td>
                  <td className="py-4 px-4 text-green-600 font-semibold">Full value or premium</td>
                  <td className="py-4 px-4 text-slate-600">60-90 days</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Mixed lease terms, mostly 3-18 months</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-teal-600 font-semibold">
                      <i className="ri-checkbox-circle-line"></i>
                      Good
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">Full value</td>
                  <td className="py-4 px-4 text-slate-600">90-120 days</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">30%+ leases expiring within 90 days</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                      <i className="ri-error-warning-line"></i>
                      Concerning
                    </span>
                  </td>
                  <td className="py-4 px-4 text-amber-600 font-semibold">-5% to -15%</td>
                  <td className="py-4 px-4 text-slate-600">120-180 days</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">High % of month-to-month tenancies</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                      <i className="ri-alert-line"></i>
                      Mixed
                    </span>
                  </td>
                  <td className="py-4 px-4 text-amber-600 font-semibold">-5% to -10%</td>
                  <td className="py-4 px-4 text-slate-600">90-150 days</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-700">Most leases 2+ years remaining</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-blue-600 font-semibold">
                      <i className="ri-information-line"></i>
                      Niche
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">Depends on rates</td>
                  <td className="py-4 px-4 text-slate-600">120-180 days</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-slate-600 mt-6 italic">
            * Impacts vary by market conditions, property type, and buyer pool
          </p>
        </div>
      </div>
    </section>
  );
}
